// AdvancedFilterModal.tsx - UI REFRESH
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import * as actions from '../../Redux/Actions'; 
import "react-datepicker/dist/react-datepicker.css";
import './AdvancedFilterModal.scss'; // <-- Make sure to create this new SCSS file

export interface IAdvancedFilters {
  dateRangeType: 'dueDate' | 'createdAt' | 'updatedAt';
  startDate: string | null;
  endDate: string | null;
  hrbpIds: string[];
  statuses: string[];
  age: string | null;
  clientIds: string[];
}

interface AdvancedFilterModalProps {
  show: boolean;
  onHide: () => void;
  currentFilters: IAdvancedFilters;
  onApplyFilters: (filters: IAdvancedFilters) => void;
  onClearFilters: () => void;
}

interface SelectOption {
  value: string;
  label: string;
}

const AdvancedFilterModal: React.FC<AdvancedFilterModalProps> = ({ 
  show, onHide, currentFilters, onApplyFilters, onClearFilters 
}) => {
  const dispatch = useDispatch();
  const [localFilters, setLocalFilters] = useState<IAdvancedFilters>(currentFilters);

  const { relevantHrbpList, getClientNameDetails } = useSelector(
    (state: any) => state.employeeReducer
  );

  useEffect(() => {
    if (show) {
      if (!relevantHrbpList) {
        dispatch(actions.getRelevantHrbpsAction());
      }
      if (!getClientNameDetails?.data) dispatch(actions.getClientNameAction());
    }
  }, [show, dispatch, relevantHrbpList, getClientNameDetails]);

  const hrbpOptions: SelectOption[] = (relevantHrbpList?.data || []).map((emp: any) => ({
    value: emp._id,
    label: `${emp.candidateName} ${emp.lastname || ""}`
  }));

  const clientOptions: SelectOption[] = (getClientNameDetails?.data || []).map((client: any) => ({
    value: client._id,
    label: client.clientName
  }));

  useEffect(() => {
    setLocalFilters(currentFilters);
  }, [currentFilters]);
  
  // --- All your handler functions remain UNCHANGED ---
  const handleMultiSelectChange = (selectedOptions: any, fieldName: keyof IAdvancedFilters) => {
    const values = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
    setLocalFilters(prev => ({ ...prev, [fieldName]: values }));
  };

  const handleCheckboxChange = (status: string) => {
    const currentStatuses = localFilters.statuses;
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter(s => s !== status) 
      : [...currentStatuses, status]; 
    setLocalFilters(prev => ({ ...prev, statuses: newStatuses }));
  };

  const handleApply = () => {
    onApplyFilters(localFilters); 
    onHide(); 
  };
  
  const handleClear = () => {
      onClearFilters();
      setLocalFilters(currentFilters); 
  };
  
  const STATUS_OPTIONS = ['Open', 'Overdue', 'Closed',];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered dialogClassName="advanced-filter-modal">
      <Modal.Header closeButton>
        <Modal.Title>Advanced Filters</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="filter-form-container">
          
          {/* --- Status Section --- */}
          <div className="filter-section">
            <h6 className="filter-section-title">Status</h6>
            <div className="status-tags-container">
              {STATUS_OPTIONS.map(status => (
                <div 
                  key={status}
                  className={`status-tag status-${status.toLowerCase()} ${localFilters.statuses.includes(status) ? 'active' : ''}`}
                  onClick={() => handleCheckboxChange(status)}
                >
                  {status}
                </div>
              ))}
            </div>
          </div>

          {/* --- Date Range Section --- */}
          <div className="filter-section">
            <h6 className="filter-section-title">Date Range</h6>
            <div className="row">
              <div className="col-md-4">
                <label className="form-label">Based On</label>
                <select className="form-select" value={localFilters.dateRangeType} onChange={e => setLocalFilters({...localFilters, dateRangeType: e.target.value as any})}>
                  <option value="dueDate">Due Date</option>
                  <option value="createdAt">Creation Date</option>
                  <option value="updatedAt">Last Updated</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Start Date</label>
                <DatePicker
                  selected={localFilters.startDate ? new Date(localFilters.startDate) : null}
                  onChange={(date) => setLocalFilters({...localFilters, startDate: date ? date.toISOString() : null})}
                  className="form-control"
                  isClearable
                  placeholderText="Select start date"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">End Date</label>
                <DatePicker
                  selected={localFilters.endDate ? new Date(localFilters.endDate) : null}
                  onChange={(date) => setLocalFilters({...localFilters, endDate: date ? date.toISOString() : null})}
                  className="form-control"
                  isClearable
                  placeholderText="Select end date"
                />
              </div>
            </div>
          </div>

          {/* --- People & Clients Section --- */}
          <div className="filter-section">
            <h6 className="filter-section-title">People & Clients</h6>
            <div className="row">
              <div className="col-md-6">
                <label className="form-label">Assigned HRBP</label>
                <Select
                  isMulti
                  options={hrbpOptions}
                  value={hrbpOptions.filter(o => localFilters.hrbpIds.includes(o.value))}
                  onChange={(selected) => handleMultiSelectChange(selected, 'hrbpIds')}
                  placeholder="Select HRBP(s)..."
                  classNamePrefix="react-select"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Client</label>
                <Select
                  isMulti
                  options={clientOptions}
                  value={clientOptions.filter(o => localFilters.clientIds.includes(o.value))}
                  onChange={(selected) => handleMultiSelectChange(selected, 'clientIds')}
                  placeholder="Select Client(s)..."
                  classNamePrefix="react-select"
                />
              </div>
            </div>
          </div>

          {/* --- Task Age Section --- */}
          <div className="filter-section">
            <h6 className="filter-section-title">Task Age</h6>
            <div className="row">
                <div className="col-md-6">
                    <label className="form-label">Age since creation</label>
                    <select className="form-select" value={localFilters.age || ''} onChange={e => setLocalFilters({...localFilters, age: e.target.value || null})}>
                        <option value="">All Ages</option>
                        <option value="0-7">New (≤ 7 days)</option>
                        <option value="8-30">8 - 30 days</option>
                        <option value="31-60">31 - 60 days</option>
                        <option value="61+">Over 60 days</option>
                    </select>
                </div>
            </div>
          </div>

        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClear}>Clear All Filters</Button>
        <Button variant="primary" onClick={handleApply}>Apply Filters</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AdvancedFilterModal;